import {
  Compass,
  ClipboardList,
  FileSignature,
  ShieldCheck,
  Network,
  Wrench,
  Globe2,
  ArrowUpRight,
} from "lucide-react";
import { useLang } from "../../i18n/LanguageContext";

const SERVICE_ICONS = [
  Compass,
  ClipboardList,
  FileSignature,
  ShieldCheck,
  Network,
  Wrench,
  Globe2,
];

// Tetris-style bento layout: 4-col grid, 7 cards.
const LAYOUT = [
  "md:col-span-2 md:row-span-1", // A
  "md:col-span-1 md:row-span-1", // B
  "md:col-span-1 md:row-span-1", // C
  "md:col-span-1 md:row-span-1", // D
  "md:col-span-1 md:row-span-1", // E
  "md:col-span-2 md:row-span-1", // F
  "md:col-span-2 md:row-span-1", // G
];

export const Services = () => {
  const { t } = useLang();

  return (
    <section
      id="services"
      data-testid="services-section"
      className="relative py-16 lg:py-20 border-t border-white/5"
    >
      <div className="absolute top-1/2 -translate-y-1/2 -right-40 w-[480px] h-[480px] rounded-full bg-[#2563EB]/15 blur-[140px] -z-10" />

      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-3xl">
          <div className="inline-flex items-center gap-2 text-[#00E5FF] font-mono text-xs uppercase tracking-[0.24em]">
            <span className="w-6 h-px bg-[#00E5FF]" />
            {t.services.eyebrow}
          </div>
          <h2
            data-testid="services-title"
            className="mt-5 font-heading font-bold text-white text-3xl sm:text-4xl lg:text-5xl leading-[1.1] tracking-tight"
          >
            {t.services.title}
          </h2>
          <p className="mt-5 text-slate-300 text-base sm:text-lg leading-relaxed">
            {t.services.subtitle}
          </p>
        </div>

        <div className="mt-14 grid grid-cols-1 md:grid-cols-4 gap-4 lg:gap-5 auto-rows-fr">
          {t.services.items.map((item, i) => {
            const Icon = SERVICE_ICONS[i];
            return (
              <article
                key={i}
                data-testid={`service-card-${item.tag}`}
                className={`group relative border border-white/10 bg-[#151B2B] p-6 lg:p-7 rounded-sm overflow-hidden hover:border-[#00E5FF]/50 hover:-translate-y-1 transition-[transform,border-color,box-shadow] duration-300 hover:shadow-[0_20px_60px_-20px_rgba(0,229,255,0.25)] ${LAYOUT[i]}`}
              >
                <div className="flex items-start justify-between mb-6">
                  <span className="inline-flex items-center justify-center w-10 h-10 border border-[#00E5FF]/30 bg-[#00E5FF]/5 rounded-sm">
                    <Icon
                      size={18}
                      strokeWidth={1.5}
                      className="text-[#00E5FF] transition-transform duration-300 group-hover:scale-110"
                    />
                  </span>
                  <span className="font-mono text-[10px] uppercase tracking-widest text-slate-500">
                    Service · {item.tag}
                  </span>
                </div>

                <h3 className="font-heading font-bold text-white text-lg lg:text-xl leading-snug tracking-tight">
                  {item.title}
                </h3>
                <p className="mt-3 text-sm text-slate-400 leading-relaxed">
                  {item.body}
                </p>

                <span
                  aria-hidden="true"
                  className="absolute bottom-5 right-5 text-slate-600 opacity-0 group-hover:opacity-100 group-hover:text-[#00E5FF] transition-[opacity,color] duration-300"
                >
                  <ArrowUpRight size={18} strokeWidth={1.5} />
                </span>

                <span
                  aria-hidden="true"
                  className="pointer-events-none absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-[#00E5FF]/60 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300"
                />
              </article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
